<template>
  <div>
    <orSeparator style="max-width: 160px" class="mx-auto" />
    <div class="d-flex justify-center mt-4">
      <v-btn
        color="primary"
        outlined
        @click.stop="addArea(value.risks.length + 1)"
      >
        {{ $t("riskAssessment.addRisk") }}
      </v-btn>
    </div>
    <v-row v-if="value.risks.length" class="mb-4 mt-2">
      <div class="subtitle-1">{{ $t("riskAssessment.risks.categories") }}</div>
    </v-row>
    <v-expansion-panels v-model="openRisks" multiple>
      <v-expansion-panel v-for="(risk, i) in value.risks" :key="i">
        <v-expansion-panel-header>
          <div class="d-flex align-center" style="width: 100%">
            <span class="text-truncate">
              <template v-if="isAdvanced && risk.stepTitle">
                <b>{{ risk.stepTitle }}</b>
                <span v-if="risk.title"> – {{ risk.title }}</span>
              </template>
              <template v-else>{{ risk.title }}</template>
            </span>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <div
                  v-on="on"
                  class="risk-badge mr-1"
                  :class="`risk-badge--${riskLevel(risk.riskClassification)}`"
                >
                  {{ risk.riskClassification }}
                </div>
              </template>
              <span>{{ $t("riskAssessment.risks.risk") }}</span>
            </v-tooltip>
            <v-icon small class="risk-badge-arrow mr-1">arrow_forward</v-icon>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <div
                  v-on="on"
                  class="risk-badge mr-3"
                  :class="`risk-badge--${riskLevel(
                    risk.riskClassificationAfterAction,
                  )}`"
                >
                  {{ risk.riskClassificationAfterAction }}
                </div>
              </template>
              <span>{{ $t("riskAssessment.risks.riskAfterMeasure") }}</span>
            </v-tooltip>
            <template v-if="isAdvanced && risk.furtherMeasures">
              <v-icon small class="risk-badge-arrow mr-1 ml-n2">arrow_forward</v-icon>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <div
                    v-on="on"
                    class="risk-badge mr-3"
                    :class="`risk-badge--${riskLevel(
                      risk.riskClassificationAfterFurtherMeasures,
                    )}`"
                  >
                    {{ risk.riskClassificationAfterFurtherMeasures || 0 }}
                  </div>
                </template>
                <span>{{ $t("riskAssessment.advanced.riskAfterFurtherMeasures") }}</span>
              </v-tooltip>
            </template>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-text-field
            v-if="isAdvanced"
            class="mb-6"
            v-model="risk.stepTitle"
            :label="$t('riskAssessment.advanced.stepTitle')"
            dense
            hide-details
          ></v-text-field>
          <v-text-field
            class="mb-6"
            v-model="risk.title"
            :label="$t('riskAssessment.risks.name')"
            data-cy="riskName"
            dense
            hide-details
          ></v-text-field>
          <template v-if="isAdvanced">
            <div class="caption">{{ $t("riskAssessment.advanced.hazardFactor") }}</div>
            <riskHazardFactorDialog :risk="risk" class="mb-6" />
            <v-textarea
              v-model="risk.hazardDescription"
              :label="$t('riskAssessment.advanced.hazardDescription')"
              auto-grow
              rows="2"
              outlined
              dense
              hide-details
              class="mb-6"
            ></v-textarea>
            <div class="caption">{{ $t("riskAssessment.advanced.measureType") }}</div>
            <template v-if="risk.stopMeasures">
              <v-tabs
                :value="stopTabOf(i)"
                grow
                :show-arrows="false"
                class="stop-tabs mb-4"
                @change="setStopTab(i, $event)"
              >
                <v-tab
                  v-for="type in measureTypes"
                  :key="type.value"
                  class="stop-tab"
                >
                  {{ $t(`riskAssessment.advanced.stop.${type.value}`) }}
                </v-tab>
              </v-tabs>
              <div
                v-for="(type, ti) in measureTypes"
                v-show="stopTabOf(i) === ti"
                :key="`stop-${type.value}`"
              >
                <v-text-field
                  v-model="risk.stopMeasures[type.value].text"
                  :label="$t('riskAssessment.advanced.measureTypeText')"
                  dense
                  hide-details
                  class="mb-6"
                ></v-text-field>
                <div class="caption">{{ $t("riskAssessment.risks.protectiveMeasure") }}</div>
                <quill-editor
                  class="largeTextField mb-6"
                  v-model="risk.stopMeasures[type.value].action"
                  :options="editorOptionAvail"
                ></quill-editor>
                <div class="caption">{{ $t("riskAssessment.risks.inspection") }}</div>
                <quill-editor
                  class="largeTextField mb-6"
                  v-model="risk.stopMeasures[type.value].measurement"
                  :options="editorOptionAvail"
                ></quill-editor>
              </div>
            </template>
          </template>
          <template v-else>
            <div class="caption">{{ $t("riskAssessment.risks.protectiveMeasure") }}</div>
            <quill-editor
              class="largeTextField mb-6"
              data-cy="action"
              v-model="risk.action"
              :options="editorOptionAvail"
            ></quill-editor>
            <div class="caption">{{ $t("riskAssessment.risks.inspection") }}</div>
            <quill-editor
              class="largeTextField mb-6"
              data-cy="measurement"
              v-model="risk.measurement"
              :options="editorOptionAvail"
            ></quill-editor>
          </template>
          <v-text-field
            v-if="!isAdvanced"
            class="mb-6 mt-6"
            v-model="risk.legal"
            :label="$t('riskAssessment.risks.legalNorm')"
            data-cy="legal"
            dense
            hide-details
          ></v-text-field>
          <v-switch
            v-model="risk.relevantForPregnant"
            :label="$t('riskAssessment.risks.relevantForPregnant')"
          ></v-switch>
          <template v-if="risk.relevantForPregnant">
            <div class="caption">{{ $t("riskAssessment.risks.measuresForPregnant") }}</div>
            <quill-editor
              class="largeTextField mb-6"
              data-cy="actionForPregnant"
              v-model="risk.actionForPregnant"
              :options="editorOptionAvail"
            ></quill-editor>
          </template>
          <template v-if="isAdvanced">
            <v-switch
              v-model="risk.furtherMeasures"
              :label="$t('riskAssessment.advanced.furtherMeasures')"
            ></v-switch>
            <template v-if="risk.furtherMeasures">
              <div class="caption">{{ $t("riskAssessment.advanced.furtherMeasuresText") }}</div>
              <quill-editor
                class="largeTextField mb-6"
                v-model="risk.furtherMeasuresText"
                :options="editorOptionAvail"
              ></quill-editor>
            </template>
          </template>
          <!-- <v-autocomplete
            v-model="risk.responsible"
            :items="userDataMap"
            :loading="getAllUsersStatus"
            hide-no-data
            item-text="Description"
            item-value="id"
            :label="$t('riskAssessment.risks.responsible')"
            data-cy="users"
            dense
            :rules="config.rules"
          ></v-autocomplete> -->
          <v-row>
            <v-col cols="12" md="6">
              <riskDialog
                v-model="value.risks[i].riskClassification"
                :dialogState="value.risks[i].dialogs.classificationDialog"
              />
              <v-btn
                @click="value.risks[i].dialogs.classificationDialog.open = true"
                block
                outlined
                :color="riskColor(value.risks[i].riskClassification)"
              >
                {{ $t("riskAssessment.risks.risk") }}: {{ value.risks[i].riskClassification }}
              </v-btn>
            </v-col>
            <v-col cols="12" md="6">
              <riskDialog
                v-model="value.risks[i].riskClassificationAfterAction"
                :dialogState="value.risks[i].dialogs.afterActionDialog"
              />
              <v-btn
                @click="value.risks[i].dialogs.afterActionDialog.open = true"
                block
                outlined
                :color="riskColor(value.risks[i].riskClassificationAfterAction)"
              >
                {{ $t("riskAssessment.risks.riskAfterMeasure") }}:
                {{ value.risks[i].riskClassificationAfterAction }}
              </v-btn>
            </v-col>
            <v-col v-if="isAdvanced && risk.furtherMeasures" cols="12" class="pt-0">
              <v-btn
                @click="openFurtherDialog(i)"
                block
                outlined
                :color="riskColor(risk.riskClassificationAfterFurtherMeasures || 0)"
              >
                {{ $t("riskAssessment.advanced.riskAfterFurtherMeasures") }}:
                {{ risk.riskClassificationAfterFurtherMeasures || 0 }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-if="isAdvanced && !isTemplate" class="mt-2">
            <v-col cols="12" md="6">
              <riskDateField
                v-model="risk.implementBy"
                :label="$t('riskAssessment.advanced.implementBy')"
              />
            </v-col>
            <v-col cols="12" md="6">
              <riskDateField
                v-model="risk.effectiveFrom"
                :label="$t('riskAssessment.advanced.effectiveFrom')"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="risk.responsibleUser"
                :items="userDataMap"
                :loading="getAllUsersStatus"
                hide-no-data
                item-text="Description"
                item-value="id"
                :label="$t('riskAssessment.advanced.responsibleUser')"
                clearable
                dense
                hide-details
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-end">
              <v-btn
                v-if="canCreateTickets || !!risk.ticket"
                color="primary"
                outlined
                height="34"
                min-width="240"
                :loading="creatingTicketIndex === i"
                :disabled="(!risk.title && !risk.stepTitle) || !!risk.ticket"
                @click="createTicket(risk, i)"
              >
                <span v-if="!risk.ticket">
                  {{ $t("inspections.createTicket") }}
                  <v-icon class="ml-1" small>launch</v-icon>
                </span>
                <span v-else>
                  {{ $t("inspections.ticketCreated") }}
                  <v-icon class="ml-1" small>check</v-icon>
                </span>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-switch
                v-model="risk.involvesThirdParties"
                :label="$t('riskAssessment.risks.involvesThirdParties')"
              ></v-switch>
            </v-col>
            <v-col>
              <v-switch
                v-model="risk.requiresSpecialTraining"
                :label="$t('riskAssessment.risks.requiresSpecialTraining')"
              ></v-switch>
            </v-col>
          </v-row>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                medium
                class="mr-2 mt-3"
                v-on="on"
                @click.stop="addArea(i)"
                data-cy="add"
              >
                add
              </v-icon>
            </template>
            <span>{{ $t("riskAssessment.risks.addHazard") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                medium
                class="mr-2 mt-3"
                v-on="on"
                @click.stop="removeArea(i)"
                data-cy="delete"
              >
                delete
              </v-icon>
            </template>
            <span>{{ $t("riskAssessment.risks.deleteHazard") }}</span>
          </v-tooltip>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <riskDialog
      v-if="isAdvanced"
      :value="furtherValue"
      :dialogState="furtherDialog"
      @input="setFurtherValue"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import i18n from "../../../i18n";
import riskDialog from "./riskNohlDialog.vue";
import riskHazardFactorDialog from "./riskHazardFactorDialog.vue";
import riskDateField from "./riskDateField.vue";
import OrSeparator from "@/components/layout/orSeperator.vue";
import {
  STOP_MEASURE_TYPES,
  STOP_KEYS,
  stopMeasuresOf,
  stopHasContent,
} from "@/data/hazardFactors";
import { newRisk } from "./newRisk";

export default {
  name: "risk-assessment-risk",
  props: ["value", "config"],
  components: { OrSeparator, riskDialog, riskHazardFactorDialog, riskDateField },
  data: () => ({
    openRisks: [0],
    furtherDialog: { open: false },
    furtherIndex: null,
    creatingTicketIndex: null,
    stopTabs: {},
    editorOptionAvail: {
      placeholder: i18n.t("riskAssessment.risks.editorPlaceholder"),
      modules: {
        toolbar: true,
      },
    },
  }),
  mounted() {
    this.$store.dispatch("getUsers");
  },
  watch: {
    "value.risks": {
      immediate: true,
      handler(risks) {
        if (!this.isAdvanced || !Array.isArray(risks)) return;
        risks.forEach((risk) => {
          if (!risk || this.stopReady(risk)) return;
          this.$set(risk, "stopMeasures", stopMeasuresOf(risk));
        });
      },
    },
  },
  computed: {
    ...mapGetters(["getAllUsers", "getAllUsersStatus"]),
    isAdvanced() {
      return !!(this.config && this.config.advanced);
    },
    isTemplate() {
      return !!(this.config && this.config.template);
    },
    measureTypes() {
      return STOP_MEASURE_TYPES;
    },
    furtherValue() {
      const risk = this.value.risks[this.furtherIndex];
      return Number((risk && risk.riskClassificationAfterFurtherMeasures) || 0);
    },
    userDataMap() {
      return this.getAllUsers.map((user) => ({
        Description: `${user.name} - ${user.short}`,
        id: user._id,
        ...user,
      }));
    },
  },
  methods: {
    addArea(index) {
      this.value.risks.splice(index + 1, 0, newRisk());
      this.openRisks.push(index + 1);
    },
    removeArea(index) {
      this.value.risks.splice(index, 1);
    },
    stopReady(risk) {
      const stop = risk.stopMeasures;
      if (!stop || !STOP_KEYS.every((key) => stop[key])) return false;
      if (STOP_KEYS.some((key) => stopHasContent(stop[key]))) return true;
      return !stopHasContent({
        text: risk.measureTypeText,
        action: risk.action,
        measurement: risk.measurement,
      });
    },
    stopTabOf(index) {
      if (this.stopTabs[index] !== undefined) return this.stopTabs[index];
      const risk = this.value.risks[index];
      const first = STOP_KEYS.findIndex(
        (key) => risk && risk.stopMeasures && stopHasContent(risk.stopMeasures[key])
      );
      return first >= 0 ? first : 0;
    },
    setStopTab(index, tab) {
      this.$set(this.stopTabs, index, tab);
    },
    openFurtherDialog(index) {
      this.furtherIndex = index;
      this.furtherDialog.open = true;
    },
    setFurtherValue(val) {
      const risk = this.value.risks[this.furtherIndex];
      if (risk) this.$set(risk, "riskClassificationAfterFurtherMeasures", val);
    },
    async createTicket(risk, index) {
      this.creatingTicketIndex = index;
      try {
        const board = await this.$store.dispatch(
          "getSettingData",
          "standardOpenBoard"
        );
        const stop = risk.stopMeasures || {};
        const stopText = STOP_KEYS.filter((key) => stopHasContent(stop[key]))
          .map(
            (key) =>
              `<b>${key} – ${this.$t(`riskAssessment.advanced.stop.${key}`)}</b><br />${[
                stop[key].text,
                stop[key].action,
              ]
                .filter(Boolean)
                .join("<br />")}`
          )
          .join("<br />");
        const description = [
          risk.hazardDescription,
          risk.furtherMeasures ? risk.furtherMeasuresText : stopText,
        ]
          .filter(Boolean)
          .join("<br />");
        const ticket = await this.$store.dispatch("addTicket", {
          recurring: 0,
          title: [this.value.title, risk.stepTitle, risk.title]
            .filter(Boolean)
            .join(" – "),
          description,
          dueDate: risk.implementBy || null,
          users: risk.responsibleUser ? [risk.responsibleUser] : [],
          priority: 1,
          board: board.value,
        });
        if (ticket) this.$set(risk, "ticket", ticket._id);
      } finally {
        this.creatingTicketIndex = null;
      }
    },
    riskColor(risk) {
      if (risk < 3) return "success";
      if (risk < 5) return "warning";
      return "error";
    },
    riskLevel(risk) {
      if (risk < 3) return "low";
      if (risk < 5) return "mid";
      return "high";
    },
  },
};
</script>

<style scoped>
.largeTextField > .ql-container {
  min-height: 120px !important;
  font-size: 16px !important;
}

.largeTextField > .ql-toolbar {
  font-size: 16px !important;
}
.stop-tab {
  min-width: 0;
  padding: 0 8px;
  text-transform: none;
  letter-spacing: normal;
  font-size: 14px;
}
.risk-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 26px;
  padding: 0 8px;
  border-radius: 13px;
  border: 1.5px solid currentColor;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
}
.risk-badge--low {
  color: #2e7d5b;
}
.risk-badge--mid {
  color: #b7791f;
}
.risk-badge--high {
  color: #c0392b;
}
.risk-badge-arrow {
  color: rgba(0, 0, 0, 0.3) !important;
}
</style>
